import React from "react";
import TimeClockHeader from "./TimeClockHeader";
import { PurpleSwitch } from "../../../helpers/helpers";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import CircularProgress from "@mui/material/CircularProgress";
import {
  API_get_timecard_config,
  API_post_timecard_config,
  API_get_company_timecard_config,
  API_post_company_timecard_config,
} from "../../../constants";
import { getRightworkAPI } from "../requests/utilities/requests";
import { postRightworkAPI } from "../requests/utilities/requests";
import Tooltip from "@mui/material/Tooltip";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import LoadSpinner from "../../../utilities/LoadSpinner";
import { createConfigAutoSaveQueue } from "./configAutoSaveQueue";

// Parse a time string (HH:mm or HH:mm:ss) into a dayjs object
function parseTimeString(timeStr) {
  if (!timeStr) return null;
  const [hours, minutes] = timeStr.split(':');
  return dayjs().hour(parseInt(hours, 10)).minute(parseInt(minutes, 10)).second(0);
}

const controlSx = {
  borderRadius: "8px",
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#6d28d9" },
  height: "36px",
  backgroundColor: "white",
  fontSize: "0.75rem",
};

const minuteOpts = (arr) => arr.map((m) => ({ value: m, label: `${m} min` }));
const hourOpts = (arr) => arr.map((h) => ({ value: h, label: `${h} hrs` }));

// MUI's TimePicker emits intermediate, INVALID Dayjs values while a time is
// being typed. Those must never reach the API: an invalid Dayjs formats to
// "Invalid Date", and post_timecard_config parses this field with int(parts[0]).
//   null  -> explicit clear
//   valid -> "HH:mm"
//   invalid -> omit the key entirely, so the stored time is left untouched
//   (auto_clock_out_time is one of the endpoint's conditionally-written fields).
const serializeAutoClockOutTime = (value) => {
  if (value == null) return { auto_clock_out_time: null };
  if (value.isValid?.()) return { auto_clock_out_time: value.format("HH:mm") };
  return {};
};

// Build the FULL config payload. Auto-save must send every field:
// post_timecard_config writes a block of fields unconditionally (forgiveness
// bounds, require-shift, break-attestation) and would null any omitted ones.
const buildTimecardPayloadFor = (storeId, data) => ({
  store_id: storeId,
  clock_in_forgiveness_lower_bound: data.clock_in_forgiveness_lower_bound,
  clock_in_forgiveness_upper_bound: data.clock_in_forgiveness_upper_bound,
  clock_in_require_shift_on_day: data.clock_in_require_shift_on_day,
  clock_in_require_shift_on_day_in_bounds: data.clock_in_require_shift_on_day_in_bounds,
  clock_out_forgiveness_lower_bound: data.clock_out_forgiveness_lower_bound,
  clock_out_forgiveness_upper_bound: data.clock_out_forgiveness_upper_bound,
  break_attestation_enabled: data.break_attestation_enabled,
  paid_break_tracking_enabled: data.paid_break_tracking_enabled,
  enforce_minimum_meal_break_duration_enabled: data.enforce_minimum_meal_break_duration_enabled,
  enforce_minimum_paid_break_duration_enabled: data.enforce_minimum_paid_break_duration_enabled,
  flag_clock_in_early_enabled: data.flag_clock_in_early_enabled,
  flag_clock_in_late_enabled: data.flag_clock_in_late_enabled,
  flag_clock_out_early_enabled: data.flag_clock_out_early_enabled,
  flag_clock_out_late_enabled: data.flag_clock_out_late_enabled,
  flag_no_associated_shift_enabled: data.flag_no_associated_shift_enabled,
  flag_did_not_take_paid_break_enabled: data.flag_did_not_take_paid_break_enabled,
  flag_did_not_take_meal_break_enabled: data.flag_did_not_take_meal_break_enabled,
  flag_shift_note_requires_review_enabled: data.flag_shift_note_requires_review_enabled,
  flag_no_break_on_long_shift_enabled: data.flag_no_break_on_long_shift_enabled,
  flag_short_meal_break_enabled: data.flag_short_meal_break_enabled,
  flag_daily_overtime_enabled: data.flag_daily_overtime_enabled,
  flag_daily_double_time_enabled: data.flag_daily_double_time_enabled,
  flag_weekly_overtime_enabled: data.flag_weekly_overtime_enabled,
  flag_seventh_day_overtime_enabled: data.flag_seventh_day_overtime_enabled,
  flag_minor_curfew_enabled: data.flag_minor_curfew_enabled,
  flag_split_shift_enabled: data.flag_split_shift_enabled,
  flag_auto_clocked_out_enabled: data.flag_auto_clocked_out_enabled,
  min_shift_hours_for_break: data.min_shift_hours_for_break,
  min_break_duration_minutes: data.min_break_duration_minutes,
  min_paid_break_duration_minutes: data.min_paid_break_duration_minutes,
  daily_overtime_threshold_hours: data.daily_overtime_threshold_hours,
  daily_double_time_threshold_hours: data.daily_double_time_threshold_hours,
  weekly_overtime_threshold_hours: data.weekly_overtime_threshold_hours,
  minor_curfew_start_hour: data.minor_curfew_start_hour,
  minor_curfew_end_hour: data.minor_curfew_end_hour,
  split_shift_minimum_gap_hours: data.split_shift_minimum_gap_hours,
  split_shift_spread_threshold_hours: data.split_shift_spread_threshold_hours,
  auto_clock_out_enabled: data.auto_clock_out_enabled,
  auto_clock_out_mode: data.auto_clock_out_mode,
  auto_clock_out_max_hours: data.auto_clock_out_max_hours,
  uuid: data.uuid,
  ...serializeAutoClockOutTime(data.auto_clock_out_time),
});

const postTimecardConfig = async (storeId, data) => {
  const res = await postRightworkAPI(
    API_post_timecard_config,
    buildTimecardPayloadFor(storeId, data)
  );
  if (res.status !== 200) throw new Error("Save failed");
  return res;
};

// The 8 flagging rows labor law owns. A store's jurisdiction derives them (and
// the panel renders them read-only), so a company holds no default for them:
// they are omitted from the company panel and REJECTED by the company endpoint.
const LABOR_LAW_MANAGED_FIELDS = new Set([
  "flag_no_break_on_long_shift_enabled",
  "flag_short_meal_break_enabled",
  "flag_daily_overtime_enabled",
  "flag_daily_double_time_enabled",
  "flag_weekly_overtime_enabled",
  "flag_seventh_day_overtime_enabled",
  "flag_minor_curfew_enabled",
  "flag_split_shift_enabled",
]);

// Must match COMPANY_DEFAULTABLE_TIMECARD_FIELDS in backend/clock_info/config_fields.py.
// Sending a labor-law field here would 400.
const COMPANY_DEFAULTABLE_FIELDS = [
  "clock_in_require_shift_on_day",
  "clock_in_require_shift_on_day_in_bounds",
  "clock_in_forgiveness_lower_bound",
  "clock_in_forgiveness_upper_bound",
  "clock_out_forgiveness_lower_bound",
  "clock_out_forgiveness_upper_bound",
  "break_attestation_enabled",
  "paid_break_tracking_enabled",
  "enforce_minimum_meal_break_duration_enabled",
  "min_break_duration_minutes",
  "enforce_minimum_paid_break_duration_enabled",
  "min_paid_break_duration_minutes",
  "auto_clock_out_enabled",
  "auto_clock_out_mode",
  "auto_clock_out_max_hours",
  "flag_clock_in_early_enabled",
  "flag_clock_in_late_enabled",
  "flag_clock_out_early_enabled",
  "flag_clock_out_late_enabled",
  "flag_no_associated_shift_enabled",
  "flag_did_not_take_paid_break_enabled",
  "flag_did_not_take_meal_break_enabled",
  "flag_shift_note_requires_review_enabled",
  "flag_auto_clocked_out_enabled",
];

const buildCompanyTimecardPayloadFor = (companyId, data) => {
  const payload = { company_id: companyId };
  COMPANY_DEFAULTABLE_FIELDS.forEach((field) => {
    if (data[field] !== undefined) payload[field] = data[field];
  });
  return { ...payload, ...serializeAutoClockOutTime(data.auto_clock_out_time) };
};

const postCompanyTimecardConfig = async (companyId, data) => {
  const res = await postRightworkAPI(
    API_post_company_timecard_config,
    buildCompanyTimecardPayloadFor(companyId, data)
  );
  if (res.status !== 200) throw new Error("Save failed");
  return res;
};

const timecardSaveErrorMessage = (error) =>
  error?.response?.data?.system?.message ||
  "Couldn't save Time Clock settings. Please try again.";

// Shared across every TimeEntriesSettings instance: Time Clock and Timecard
// Flagging are two views of the SAME TimecardConfig, so they must serialize
// against one another (and survive an inline panel unmounting mid-save).
const timecardQueue = createConfigAutoSaveQueue({
  // The panel was collapsed/switched before this save failed, so no mounted
  // component can toast or resync. Report it here instead of failing silently;
  // the queue cleared its cache, so the next mount refetches server state.
  onOrphanedError: (error) => {
    toast.error(timecardSaveErrorMessage(error), { autoClose: 4000 });
    console.error("Timecard auto-save failed after unmount:", error);
  },
});

function TimeEntriesSettings(props) {
  const [isLoading, setIsLoading] = React.useState(false);

  const [settingsData, setSettingsData] = React.useState({});
  const [originalSettingsJson, setOriginalSettingsJson] = React.useState("");
  const [laborLawEnabled, setLaborLawEnabled] = React.useState(false);
  // autoSave mode: each change writes immediately (like the module toggles).
  // savingField holds the field id of the in-flight change so its control can
  // show a spinner, mirroring the per-control spinner on the flag toggles.
  const [savingField, setSavingField] = React.useState(null);
  const isFieldSaving = (field) => {
    if (savingField == null || field == null) return false;
    const fields = Array.isArray(field) ? field : [field];
    return fields.includes(savingField);
  };

  // autoSave writes are serialized per config row by `timecardQueue`, which lives
  // outside React so it survives this panel unmounting mid-save (inline panels
  // unmount on collapse) and is shared with the sibling Timecard Flagging view
  // that edits the same TimecardConfig. See configAutoSaveQueue.js.
  //
  // `companyScope` edits the company's TEMPLATE rather than a store's config, so
  // it gets its own queue key and its own endpoints. The key namespace keeps a
  // company id from ever colliding with a store id.
  const queueKey = props.companyScope ? `company:${props.companyId}` : props.homeStore;
  const postForScope = props.companyScope
    ? (_key, data) => postCompanyTimecardConfig(props.companyId, data)
    : postTimecardConfig;

  // In company scope the labor-law rows are absent entirely: a company has no
  // jurisdiction, so there is nothing to show as "Set by labor law".
  const hideLaborLawRow = (field) =>
    Boolean(props.companyScope) && LABOR_LAW_MANAGED_FIELDS.has(field);

  // Serialize settings to a comparable string, normalizing the dayjs time field.
  const toComparableSettings = (data) => {
    const normalized = { ...data };
    if (normalized.auto_clock_out_time && normalized.auto_clock_out_time.format) {
      normalized.auto_clock_out_time = normalized.auto_clock_out_time.format("HH:mm");
    }
    return JSON.stringify(normalized);
  };

  const hasUnsavedChanges =
    originalSettingsJson !== "" &&
    toComparableSettings(settingsData) !== originalSettingsJson;

  const fetchTimecardConfig = async (showLoading) => {
    setIsLoading(showLoading);
    const storeId = queueKey;
    // Capture queue state BEFORE issuing the GET. A response read from the
    // server before an in-flight POST committed can still resolve after the
    // queue drains — judging staleness at resolve time would then adopt (and
    // cache) that stale snapshot, letting the next auto-save revert the edit.
    const startedBusy = timecardQueue.isBusy(storeId);
    const startVersion = timecardQueue.getVersion(storeId);
    let api_params = props.companyScope
      ? { company_id: props.companyId }
      : { store_id: props.homeStore };
    let api = props.companyScope
      ? API_get_company_timecard_config
      : API_get_timecard_config;
    try {
      const res = await getRightworkAPI(api, api_params);
      if (res.status === 200) {
        setLaborLawEnabled(res.data?.redux?.payload?.labor_law_enabled || false);
        const data = res.data?.redux?.payload?.timecard_config;
        // Normalize decimal fields from strings to numbers for Select components
        const normalizedData = {
          ...data,
          daily_overtime_threshold_hours: data.daily_overtime_threshold_hours
            ? parseFloat(data.daily_overtime_threshold_hours)
            : null,
          daily_double_time_threshold_hours: data.daily_double_time_threshold_hours
            ? parseFloat(data.daily_double_time_threshold_hours)
            : null,
          min_shift_hours_for_break: data.min_shift_hours_for_break
            ? parseFloat(data.min_shift_hours_for_break)
            : null,
          weekly_overtime_threshold_hours: data.weekly_overtime_threshold_hours
            ? parseFloat(data.weekly_overtime_threshold_hours)
            : null,
          minor_curfew_start_hour: data.minor_curfew_start_hour
            ? parseFloat(data.minor_curfew_start_hour)
            : null,
          minor_curfew_end_hour: data.minor_curfew_end_hour
            ? parseFloat(data.minor_curfew_end_hour)
            : null,
          split_shift_minimum_gap_hours: data.split_shift_minimum_gap_hours
            ? parseFloat(data.split_shift_minimum_gap_hours)
            : null,
          split_shift_spread_threshold_hours: data.split_shift_spread_threshold_hours
            ? parseFloat(data.split_shift_spread_threshold_hours)
            : null,
          auto_clock_out_max_hours: data.auto_clock_out_max_hours
            ? parseFloat(data.auto_clock_out_max_hours)
            : null,
          auto_clock_out_time: parseTimeString(data.auto_clock_out_time),
        };
        // Stale if a save was in flight when the GET started, is in flight now,
        // or a commit landed during the fetch window. Then the cache (not the
        // server snapshot) is authoritative, and we must NOT overwrite it.
        const cached = timecardQueue.getLatest(storeId);
        const useCached = Boolean(
          cached && timecardQueue.isFetchStale(storeId, startedBusy, startVersion)
        );
        const effective = useCached ? cached : normalizedData;

        setSettingsData(effective);
        if (!useCached) {
          timecardQueue.setLatest(storeId, normalizedData);
        }
        setOriginalSettingsJson(toComparableSettings(effective));
        setIsLoading(false);
      }
    } catch (error) {
      throw Error(error);
    }
  };

  React.useEffect(() => {
    // Reset settings data and refetch when the config row we're editing changes
    setSettingsData({});
    fetchTimecardConfig(true);
  }, [queueKey]);

  // Follow the shared queue while mounted: clear the spinner when it drains, and
  // resync/report if a save failed. Unsubscribing on unmount lets the queue keep
  // draining without touching this component's state.
  React.useEffect(() => {
    if (!props.autoSave) return undefined;
    const unsubscribe = timecardQueue.subscribe(queueKey, {
      onBusyChange: (busy) => {
        if (!busy) setSavingField(null);
        // Lets an owner block actions that read this config from the server
        // (e.g. pushing a company default) until the write has landed.
        props.onSavingChange?.(busy);
      },
      onError: (error) => {
        setSavingField(null);
        fetchTimecardConfig(false);
        toast.error(timecardSaveErrorMessage(error), { autoClose: 3000 });
        console.error("Error:", error);
      },
    });
    return () => {
      unsubscribe();
      props.onSavingChange?.(false);
    };
  }, [queueKey, props.autoSave]);

  // All field changes route through here. In autoSave mode the patch is merged
  // into the queue's cached snapshot (the source of truth for the payload) and a
  // serialized save is scheduled; the merged snapshot renders optimistically.
  // In non-autoSave mode it just updates state and the Save button persists.
  //
  // `save: false` applies the patch locally WITHOUT persisting — used for the
  // TimePicker's intermediate invalid values so the keystroke still renders but
  // never reaches the API (and never lands in the queue's cached snapshot).
  const commitChange = (patch, { save = true } = {}) => {
    if (props.autoSave && save) {
      // The changed field id drives the per-control spinner.
      setSavingField(Object.keys(patch)[0] ?? "__all__");
      // settingsData is the baseline if the queue's cache was cleared by a
      // failed save, so the posted payload is never partial.
      const next = timecardQueue.commit(
        queueKey,
        patch,
        postForScope,
        settingsData
      );
      setSettingsData(next);
      return;
    }
    setSettingsData((prev) => ({ ...prev, ...patch }));
  };

  const handleRequireShift = (event) => {
    commitChange({
      clock_in_require_shift_on_day: event.target.checked,
      clock_in_require_shift_on_day_in_bounds: false,
    });
  };

  const handlePreventEarlyClockIn = (event) => {
    commitChange({
      clock_in_require_shift_on_day_in_bounds: event.target.checked,
    });
  };

  const handleBreakAttestationToggle = (event) => {
    commitChange({ break_attestation_enabled: event.target.checked });
  };

  const handlePaidBreakTrackingToggle = (event) => {
    commitChange({ paid_break_tracking_enabled: event.target.checked });
  };


  // Generic handler for flag toggles
  const handleFlagToggle = (fieldName) => (event) => {
    commitChange({ [fieldName]: event.target.checked });
  };

  // Generic handler for threshold inputs
  const handleThresholdChange = (fieldName) => (event) => {
    commitChange({ [fieldName]: event.target.value });
  };

  const post_timecard_config = async () => {
    // Prepare data to send (same full payload the auto-save queue posts).
    const requestData = buildTimecardPayloadFor(props.homeStore, settingsData);

    try {
      toast.loading("Saving Time Entry Settings...", { autoClose: false });

      const res = await postRightworkAPI(API_post_timecard_config, requestData);

      if (res.status === 200) {
        toast.dismiss();
        toast.success("Time Entry Settings Saved", { autoClose: 3000 });

        fetchTimecardConfig(false);
      }
    } catch (error) {
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }

      console.error("Error:", error); // Handle error as needed
    }
  };

  // ---- Presentation helpers (plain functions — NOT components — so they
  // don't remount their subtree and drop input focus on each render). ----

  // Small spinner shown on the control being auto-saved (mirrors the flag
  // toggles). Renders nothing outside autoSave mode or when idle.
  const savingSpinner = (field) =>
    props.autoSave && isFieldSaving(field) ? (
      <CircularProgress size={14} sx={{ color: "#64748b" }} />
    ) : null;

  const renderSelect = (value, onChange, options, width = 116, field) => (
    <div className="flex items-center gap-2">
      {savingSpinner(field)}
      <div style={{ width }}>
        {/* displayEmpty so options with an empty-string value (e.g. the auto
            clock-out "Default (24 hrs)" mode) still render their label. */}
        <Select fullWidth displayEmpty value={value} onChange={onChange} size="small" sx={controlSx}>
          {options.map((o) => (
            <MenuItem key={String(o.value)} value={o.value} sx={{ fontSize: "0.75rem" }}>
              {o.label}
            </MenuItem>
          ))}
        </Select>
      </div>
    </div>
  );

  // A setting row: label (+ help tooltip) on the left, trailing control(s) +
  // switch on the right. `indent` nests a dependent row under its parent.
  // `managed` grays the row and disables its controls — used when labor law
  // governs the flag instead of this config. The threshold is hidden in that
  // case: the config value isn't what the law enforces, so showing it would
  // misrepresent the actual threshold. The row just reads "Set by labor law".
  const renderRow = ({ label, help, checked, onChange, trailing, indent, key, managed, field }) => (
    <div
      key={key}
      className={`flex items-center justify-between gap-4 py-3 ${indent ? "pl-6" : ""} ${managed ? "opacity-60" : ""}`}
    >
      <div className="flex items-center gap-1.5 min-w-0">
        <p className={`text-sm text-slate-800 ${props.inline ? "font-medium" : ""}`}>{label}</p>
        {managed ? (
          <span className="ml-1 inline-flex items-center rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-500 whitespace-nowrap">
            Set by labor law
          </span>
        ) : help ? (
          <Tooltip title={help}>
            <HelpOutlineOutlinedIcon sx={{ fontSize: 15 }} className="text-slate-400" />
          </Tooltip>
        ) : null}
      </div>
      <div className="flex items-center gap-2.5 shrink-0">
        {managed ? (
          <span className="text-xs font-medium text-emerald-600 shrink-0">
            Automatic
          </span>
        ) : (
          <>
            {savingSpinner(field)}
            {trailing}
            <PurpleSwitch checked={checked} onChange={onChange} />
          </>
        )}
      </div>
    </div>
  );

  // Inline (Manage Locations) matches the flatter module-card styling used by
  // the flag settings; non-inline (payroll full-page) keeps the larger headers.
  const renderCard = (title, description, children, banner) => (
    <div
      className={
        props.inline
          ? "rounded-lg border border-slate-200 bg-white mb-3"
          : "rounded-xl border border-slate-200 bg-white shadow-sm mb-5"
      }
    >
      <div
        className={`border-b border-slate-100 ${props.inline ? "px-3 py-2.5" : "px-5 py-4"}`}
      >
        <h2
          className={
            props.inline
              ? "text-sm font-medium text-slate-900"
              : "text-lg font-semibold text-slate-900"
          }
        >
          {title}
        </h2>
        {description && (
          <p
            className={
              props.inline
                ? "mt-0.5 text-xs leading-relaxed text-slate-500"
                : "text-sm text-slate-500 mt-0.5"
            }
          >
            {description}
          </p>
        )}
        {banner}
      </div>
      <div
        className={`divide-y divide-slate-100 ${props.inline ? "px-3" : "px-5"}`}
      >
        {children}
      </div>
    </div>
  );

  const renderGroup = (title, description, children) => (
    <div className="py-4">
      <p className="text-sm font-semibold text-slate-900">{title}</p>
      {description && <p className="text-xs text-slate-500 mt-0.5">{description}</p>}
      <div className="mt-1 divide-y divide-slate-100">{children}</div>
    </div>
  );

  // Fixed width so the select + switch align across rows regardless of the
  // unit label's length ("before shift" vs "after shift start").
  const unit = (text) => (
    <span className="inline-block w-28 text-xs text-slate-500 whitespace-nowrap">
      {text}
    </span>
  );

  return (
    <div>
      {/* embedded: rendered inside the Manage Locations config drawer, which
          is already scoped to one location and supplies its own header, so the
          payroll-flow header (store switcher + settings gear + tab nav) is
          suppressed. */}
      {!props.embedded && (
        <div>
          <TimeClockHeader
            title={props.title}
            storeOptions={props.storeOptions}
            homeStore={props.homeStore}
            storeNames={props.storeNames}
            change_home_store={props.change_home_store}
            setCurrentTab={props.setCurrentTab}
            setShowSettings={props.setShowSettings}
            showSettings={props.showSettings}
            timeEntriesEnabled={props.timeEntriesEnabled}
          />
        </div>
      )}
      {!isLoading ? (
        <div className={props.inline ? "px-1 py-2" : "px-6 pt-6 pb-16 max-w-4xl mx-auto"}>
          {/* Inline: the accordion row already labels + describes this surface,
              so the panel's own header (intro + Save button) is redundant. */}
          {!props.inline && (
            <div className="flex items-start justify-between gap-4 mb-6">
            <div>
              {!props.embedded && (
                <h1 className="text-2xl text-slate-900">Settings</h1>
              )}
              <p className={`text-sm text-slate-500 ${props.embedded ? "" : "mt-1"}`}>
                {props.section === "clock"
                  ? "Configure how clock-ins and breaks behave at this location."
                  : props.section === "flagging"
                  ? "Choose which issues automatically flag time entries for manager review."
                  : "Configure how clock-ins, breaks, and time entry flagging work at this location."}
              </p>
            </div>
            {!props.autoSave && (
              <button
                type="button"
                disabled={!hasUnsavedChanges}
                onClick={() => hasUnsavedChanges && post_timecard_config()}
                className={`shrink-0 px-5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  hasUnsavedChanges
                    ? "bg-violet-700 hover:bg-violet-800 text-white"
                    : "bg-slate-100 text-slate-400 cursor-not-allowed"
                }`}
              >
                Save changes
              </button>
            )}
          </div>
          )}

          {/* ===== Clock-In & Breaks (Rightwork time clock only) =====
              section: "clock" tab shows this; "flagging" tab hides it;
              undefined (payroll gear) shows everything. */}
          {props.section !== "flagging" && props.timeEntriesEnabled && (
            <>
              {renderCard(
                "Clock-in requirements",
                "How employees are allowed to clock in.",
                <>
                  {renderRow({
                    label: "Require shift to clock in",
                    help: "When enabled, employees will not be able to clock-in when they don't have a shift scheduled for that day.",
                    checked: settingsData.clock_in_require_shift_on_day ?? false,
                    onChange: handleRequireShift,
                    field: "clock_in_require_shift_on_day",
                  })}
                  {settingsData.clock_in_require_shift_on_day &&
                    renderRow({
                      indent: true,
                      label: "Prevent early clock-ins",
                      // The window is ONE column, also rendered on Timecard
                      // Flagging as the early clock-in threshold. Flagging is its
                      // only home: it shows all four forgiveness bounds, always,
                      // and is the only surface that pushes them.
                      //
                      // A location may state its window, because that is a fact
                      // about the location. The company default must NOT: the
                      // clock surface does not carry the window, so a number here
                      // would promise an Apply that keeps each target's own value.
                      // Name the owner instead.
                      help: "Employees may clock in this many minutes before their shift. The window is the early clock-in threshold in Time Entry Flagging.",
                      checked:
                        settingsData.clock_in_require_shift_on_day_in_bounds ?? false,
                      onChange: handlePreventEarlyClockIn,
                      field: "clock_in_require_shift_on_day_in_bounds",
                      trailing: settingsData.clock_in_require_shift_on_day_in_bounds &&
                        (props.companyScope ? (
                          <span className="text-xs text-slate-400 whitespace-nowrap">
                            Window set in Time Entry Flagging
                          </span>
                        ) : (
                          <span className="text-xs text-slate-500 whitespace-nowrap">
                            {settingsData.clock_in_forgiveness_lower_bound ?? 10} min
                            before shift
                          </span>
                        )),
                    })}
                </>,
              )}

              {renderCard(
                "Breaks",
                "Break attestation, paid break tracking, and minimum durations.",
                <>
                  {renderRow({
                    label: "Require attestation for scheduled paid and meal breaks",
                    help: "When enabled, employees will confirm whether they took their scheduled paid breaks and meal breaks when clocking out. If a meal break is confirmed as taken, the system will ensure at least a 30-minute break is recorded.",
                    checked: settingsData.break_attestation_enabled ?? false,
                    onChange: handleBreakAttestationToggle,
                    field: "break_attestation_enabled",
                  })}
                  {renderRow({
                    label: props.timeEntriesEnabled
                      ? "Allow employees to clock in and out of paid breaks"
                      : "Show paid break time on time entries",
                    help: props.timeEntriesEnabled
                      ? "When enabled, employees will see separate 'Paid Break' and 'Unpaid Break' buttons on the time clock instead of a single 'Start Break' button."
                      : "When enabled, a Paid Breaks column appears on time entries, populated from paid breaks recorded by your POS.",
                    checked: settingsData.paid_break_tracking_enabled ?? false,
                    onChange: handlePaidBreakTrackingToggle,
                    field: "paid_break_tracking_enabled",
                  })}
                  {renderRow({
                    label: "Enforce minimum meal break duration",
                    help: "When enabled, employees cannot end a meal break on the time clock until the selected minimum duration has elapsed.",
                    checked:
                      settingsData.enforce_minimum_meal_break_duration_enabled ?? false,
                    onChange: handleFlagToggle(
                      "enforce_minimum_meal_break_duration_enabled",
                    ),
                    field: "enforce_minimum_meal_break_duration_enabled",
                    trailing:
                      settingsData.enforce_minimum_meal_break_duration_enabled &&
                      renderSelect(
                        settingsData.min_break_duration_minutes ?? 30,
                        handleThresholdChange("min_break_duration_minutes"),
                        minuteOpts([15, 20, 30, 45, 60]),
                        116,
                        "min_break_duration_minutes",
                      ),
                  })}
                  {settingsData.paid_break_tracking_enabled &&
                    renderRow({
                      label: "Enforce minimum paid break duration",
                      help: "When enabled, employees cannot end a paid break on the time clock until the selected minimum duration has elapsed.",
                      checked:
                        settingsData.enforce_minimum_paid_break_duration_enabled ?? false,
                      onChange: handleFlagToggle(
                        "enforce_minimum_paid_break_duration_enabled",
                      ),
                      field: "enforce_minimum_paid_break_duration_enabled",
                      trailing:
                        settingsData.enforce_minimum_paid_break_duration_enabled &&
                        renderSelect(
                          settingsData.min_paid_break_duration_minutes ?? 10,
                          handleThresholdChange("min_paid_break_duration_minutes"),
                          minuteOpts([5, 10, 15, 20]),
                          116,
                          "min_paid_break_duration_minutes",
                        ),
                    })}
                </>,
              )}

              {renderCard(
                "Auto clock-out",
                "Automatically close time entries left open too long.",
                <>
                  {renderRow({
                    label: "Automatically clock out stale time entries",
                    help: "When enabled, time entries that have been open too long will be automatically clocked out. When disabled, time entries remain open until manually clocked out.",
                    checked: settingsData.auto_clock_out_enabled ?? true,
                    onChange: handleFlagToggle("auto_clock_out_enabled"),
                    field: "auto_clock_out_enabled",
                  })}
                  {(settingsData.auto_clock_out_enabled ?? true) && (
                    <div className="pl-6 py-3 space-y-3">
                        <div className="flex items-center">
                          <p className="text-sm text-slate-500 w-32">Mode</p>
                          {renderSelect(
                            settingsData.auto_clock_out_mode ?? "",
                            handleThresholdChange("auto_clock_out_mode"),
                            [
                              { value: "", label: "Default (24 hrs)" },
                              { value: "max_hours", label: "Max Hours" },
                              { value: "time_of_day", label: "Time of Day" },
                              { value: "both", label: "Both (whichever is first)" },
                            ],
                            220,
                            "auto_clock_out_mode",
                          )}
                        </div>
                        {(settingsData.auto_clock_out_mode === "max_hours" ||
                          settingsData.auto_clock_out_mode === "both") && (
                          <div className="flex items-center">
                            <p className="text-sm text-slate-500 w-32">Max hours</p>
                            {renderSelect(
                              settingsData.auto_clock_out_max_hours ?? 24,
                              handleThresholdChange("auto_clock_out_max_hours"),
                              [8, 10, 12, 14, 16, 18, 20, 22, 24, 36, 48].map((h) => ({
                                value: h,
                                label: `${h} hours`,
                              })),
                              180,
                              "auto_clock_out_max_hours",
                            )}
                          </div>
                        )}
                        {(settingsData.auto_clock_out_mode === "time_of_day" ||
                          settingsData.auto_clock_out_mode === "both") && (
                          <div className="flex items-center">
                            <p className="text-sm text-slate-500 w-32">Clock-out time</p>
                            {savingSpinner("auto_clock_out_time")}
                            <div style={{ width: 180 }} className="ml-2">
                              <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <TimePicker
                                  value={settingsData.auto_clock_out_time || null}
                                  onChange={(newValue, context) => {
                                    // MUI fires onChange for intermediate values
                                    // while typing. Only persist a complete,
                                    // valid time (or an explicit clear);
                                    // otherwise just reflect the keystroke.
                                    const invalid =
                                      Boolean(context?.validationError) ||
                                      (newValue != null &&
                                        newValue.isValid?.() !== true);
                                    commitChange(
                                      { auto_clock_out_time: newValue },
                                      { save: !invalid }
                                    );
                                  }}
                                  slotProps={{
                                    textField: {
                                      size: "small",
                                      sx: {
                                        "& .MuiOutlinedInput-root": {
                                          borderRadius: "8px",
                                          height: "36px",
                                          backgroundColor: "white",
                                          "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                            { borderColor: "#6d28d9" },
                                        },
                                      },
                                    },
                                  }}
                                />
                              </LocalizationProvider>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </>,
                )}
              </>
            )}

          {/* ===== Time Entries (POS-sourced stores) ===== */}
          {props.section !== "flagging" && !props.timeEntriesEnabled &&
            renderCard(
              "Time Entries",
              "Configure how POS-sourced time entries are displayed.",
              renderGroup(
                "Paid break tracking",
                null,
                renderRow({
                  label: "Show paid break time on time entries",
                  help: "When enabled, a Paid Breaks column appears on time entries, populated from paid breaks recorded by your POS.",
                  checked: settingsData.paid_break_tracking_enabled ?? false,
                  onChange: handlePaidBreakTrackingToggle,
                  field: "paid_break_tracking_enabled",
                }),
              ),
            )}

          {/* ===== Timecard Flagging Rules — grouped into cards mirroring
              the Live Action Alerts tab. Labor-law and attestation
              conditionals from the original grouping are preserved. ===== */}
          {props.section !== "clock" && (
            <>
              {renderCard(
                "Clock time",
                "Flag when employees clock in or out beyond the forgiveness windows.",
                <>
                  {renderRow({
                    label: "Flag early clock-in",
                    checked: settingsData.flag_clock_in_early_enabled ?? true,
                    onChange: handleFlagToggle("flag_clock_in_early_enabled"),
                    field: "flag_clock_in_early_enabled",
                    trailing: (
                      <>
                        {renderSelect(
                          settingsData.clock_in_forgiveness_lower_bound ?? 10,
                          handleThresholdChange("clock_in_forgiveness_lower_bound"),
                          minuteOpts([5, 10, 15, 20, 30, 45, 60]),
                          116,
                          "clock_in_forgiveness_lower_bound",
                        )}
                        {unit("before shift")}
                      </>
                    ),
                  })}
                  {renderRow({
                    label: "Flag late clock-in",
                    checked: settingsData.flag_clock_in_late_enabled ?? true,
                    onChange: handleFlagToggle("flag_clock_in_late_enabled"),
                    field: "flag_clock_in_late_enabled",
                    trailing: (
                      <>
                        {renderSelect(
                          settingsData.clock_in_forgiveness_upper_bound ?? 10,
                          handleThresholdChange("clock_in_forgiveness_upper_bound"),
                          minuteOpts([5, 10, 15, 20, 30, 45, 60]),
                          116,
                          "clock_in_forgiveness_upper_bound",
                        )}
                        {unit("after shift start")}
                      </>
                    ),
                  })}
                  {renderRow({
                    label: "Flag early clock-out",
                    checked: settingsData.flag_clock_out_early_enabled ?? true,
                    onChange: handleFlagToggle("flag_clock_out_early_enabled"),
                    field: "flag_clock_out_early_enabled",
                    trailing: (
                      <>
                        {renderSelect(
                          settingsData.clock_out_forgiveness_lower_bound ?? 10,
                          handleThresholdChange("clock_out_forgiveness_lower_bound"),
                          minuteOpts([5, 10, 15, 20, 30, 45, 60]),
                          116,
                          "clock_out_forgiveness_lower_bound",
                        )}
                        {unit("before shift end")}
                      </>
                    ),
                  })}
                  {renderRow({
                    label: "Flag late clock-out",
                    checked: settingsData.flag_clock_out_late_enabled ?? true,
                    onChange: handleFlagToggle("flag_clock_out_late_enabled"),
                    field: "flag_clock_out_late_enabled",
                    trailing: (
                      <>
                        {renderSelect(
                          settingsData.clock_out_forgiveness_upper_bound ?? 10,
                          handleThresholdChange("clock_out_forgiveness_upper_bound"),
                          minuteOpts([5, 10, 15, 20, 30, 45, 60]),
                          116,
                          "clock_out_forgiveness_upper_bound",
                        )}
                        {unit("after shift end")}
                      </>
                    ),
                  })}
                </>,
              )}

              {renderCard(
                "Attestation & records",
                "Reviewed after clock-out or generated by the system.",
                <>
                  {settingsData.break_attestation_enabled && (
                    <>
                      {renderRow({
                        label: 'Flag "did not take paid break(s)"',
                        checked: settingsData.flag_did_not_take_paid_break_enabled ?? true,
                        onChange: handleFlagToggle("flag_did_not_take_paid_break_enabled"),
                        field: "flag_did_not_take_paid_break_enabled",
                      })}
                      {renderRow({
                        label: 'Flag "did not take meal break"',
                        checked: settingsData.flag_did_not_take_meal_break_enabled ?? true,
                        onChange: handleFlagToggle("flag_did_not_take_meal_break_enabled"),
                        field: "flag_did_not_take_meal_break_enabled",
                      })}
                      {renderRow({
                        label: "Flag when shift note is left",
                        checked: settingsData.flag_shift_note_requires_review_enabled ?? true,
                        onChange: handleFlagToggle("flag_shift_note_requires_review_enabled"),
                        field: "flag_shift_note_requires_review_enabled",
                      })}
                    </>
                  )}
                  {renderRow({
                    label: "Flag auto-clocked out time entries",
                    checked: settingsData.flag_auto_clocked_out_enabled ?? true,
                    onChange: handleFlagToggle("flag_auto_clocked_out_enabled"),
                    field: "flag_auto_clocked_out_enabled",
                  })}
                </>,
              )}

              {/* Every row in this card is labor-law governed, so the whole card
                  is absent from the company default. */}
              {!props.companyScope &&
                renderCard(
                "Breaks & overtime",
                "Break and overtime thresholds on completed hours.",
                <>
                  {!settingsData.break_attestation_enabled && (
                    <>
                      {renderRow({
                        label: "Flag no break on long shift",
                        managed: laborLawEnabled,
                        checked: settingsData.flag_no_break_on_long_shift_enabled ?? true,
                        onChange: handleFlagToggle("flag_no_break_on_long_shift_enabled"),
                        field: "flag_no_break_on_long_shift_enabled",
                        trailing: renderSelect(
                          settingsData.min_shift_hours_for_break ?? 6,
                          handleThresholdChange("min_shift_hours_for_break"),
                          hourOpts([4, 5, 6, 7, 8]),
                          116,
                          "min_shift_hours_for_break",
                        ),
                      })}
                      {!settingsData.enforce_minimum_meal_break_duration_enabled &&
                        renderRow({
                          label: "Flag short meal break",
                          managed: laborLawEnabled,
                          checked: settingsData.flag_short_meal_break_enabled ?? true,
                          onChange: handleFlagToggle("flag_short_meal_break_enabled"),
                          field: "flag_short_meal_break_enabled",
                          trailing: renderSelect(
                            settingsData.min_break_duration_minutes ?? 30,
                            handleThresholdChange("min_break_duration_minutes"),
                            minuteOpts([15, 20, 30, 45, 60]),
                            116,
                            "min_break_duration_minutes",
                          ),
                        })}
                    </>
                  )}
                  {renderRow({
                    label: "Flag daily overtime",
                    managed: laborLawEnabled,
                    checked: settingsData.flag_daily_overtime_enabled ?? true,
                    onChange: handleFlagToggle("flag_daily_overtime_enabled"),
                    field: "flag_daily_overtime_enabled",
                    trailing: renderSelect(
                      settingsData.daily_overtime_threshold_hours ?? 8,
                      handleThresholdChange("daily_overtime_threshold_hours"),
                      hourOpts([6, 7, 8, 9, 10]),
                      116,
                      "daily_overtime_threshold_hours",
                    ),
                  })}
                  {renderRow({
                    label: "Flag daily double-time",
                    managed: laborLawEnabled,
                    checked: settingsData.flag_daily_double_time_enabled ?? true,
                    onChange: handleFlagToggle("flag_daily_double_time_enabled"),
                    field: "flag_daily_double_time_enabled",
                    trailing: renderSelect(
                      settingsData.daily_double_time_threshold_hours ?? 12,
                      handleThresholdChange("daily_double_time_threshold_hours"),
                      hourOpts([10, 11, 12, 13, 14]),
                      116,
                      "daily_double_time_threshold_hours",
                    ),
                  })}
                  {renderRow({
                    label: "Flag weekly overtime",
                    managed: laborLawEnabled,
                    checked: settingsData.flag_weekly_overtime_enabled ?? false,
                    onChange: handleFlagToggle("flag_weekly_overtime_enabled"),
                    field: "flag_weekly_overtime_enabled",
                    trailing: renderSelect(
                      settingsData.weekly_overtime_threshold_hours ?? 40,
                      handleThresholdChange("weekly_overtime_threshold_hours"),
                      hourOpts([36, 38, 40, 42, 44, 48]),
                      116,
                      "weekly_overtime_threshold_hours",
                    ),
                  })}
                  {renderRow({
                    label: "Flag 7th consecutive day",
                    managed: laborLawEnabled,
                    checked: settingsData.flag_seventh_day_overtime_enabled ?? false,
                    onChange: handleFlagToggle("flag_seventh_day_overtime_enabled"),
                    field: "flag_seventh_day_overtime_enabled",
                  })}
                </>,
              )}

              {renderCard(
                props.companyScope ? "Scheduling" : "Scheduling & minors",
                "Structural checks against the schedule.",
                <>
                  {renderRow({
                    label: "Flag shifts with no associated schedule",
                    checked: settingsData.flag_no_associated_shift_enabled ?? true,
                    onChange: handleFlagToggle("flag_no_associated_shift_enabled"),
                    field: "flag_no_associated_shift_enabled",
                  })}
                  {!hideLaborLawRow("flag_minor_curfew_enabled") &&
                    renderRow({
                        label: "Flag minor curfew",
                        managed: laborLawEnabled,
                        checked: settingsData.flag_minor_curfew_enabled ?? false,
                        onChange: handleFlagToggle("flag_minor_curfew_enabled"),
                        field: "flag_minor_curfew_enabled",
                        trailing: (
                          <>
                            {renderSelect(
                              settingsData.minor_curfew_start_hour ?? 7,
                              handleThresholdChange("minor_curfew_start_hour"),
                              [5, 5.25, 5.5, 5.75, 6, 6.25, 6.5, 6.75, 7, 7.25, 7.5, 7.75, 8].map(
                                (hr) => {
                                  const mins = Math.round((hr % 1) * 60);
                                  return {
                                    value: hr,
                                    label: `${Math.floor(hr)}:${String(mins).padStart(2, "0")} AM start`,
                                  };
                                },
                              ),
                              172,
                              "minor_curfew_start_hour",
                            )}
                            {renderSelect(
                              settingsData.minor_curfew_end_hour ?? 21,
                              handleThresholdChange("minor_curfew_end_hour"),
                              [
                                19, 19.25, 19.5, 19.75, 20, 20.25, 20.5, 20.75, 21, 21.25,
                                21.5, 21.75, 22, 22.25, 22.5, 22.75, 23,
                              ].map((hr) => {
                                const mins = Math.round((hr % 1) * 60);
                                const displayHr =
                                  Math.floor(hr) > 12 ? Math.floor(hr) - 12 : Math.floor(hr);
                                return {
                                  value: hr,
                                  label: `${displayHr}:${String(mins).padStart(2, "0")} PM end`,
                                };
                              }),
                              172,
                              "minor_curfew_end_hour",
                            )}
                          </>
                        ),
                      })}
                  {!hideLaborLawRow("flag_split_shift_enabled") &&
                    renderRow({
                        label: "Flag split shifts",
                        managed: laborLawEnabled,
                        checked: settingsData.flag_split_shift_enabled ?? false,
                        onChange: handleFlagToggle("flag_split_shift_enabled"),
                        field: "flag_split_shift_enabled",
                        trailing: (
                          <>
                            {renderSelect(
                              settingsData.split_shift_minimum_gap_hours ?? 1,
                              handleThresholdChange("split_shift_minimum_gap_hours"),
                              [
                                { value: 0, label: "No gap check" },
                                ...[0.5, 1, 1.5, 2, 3].map((h) => ({
                                  value: h,
                                  label: `${h} hr gap`,
                                })),
                              ],
                              172,
                              "split_shift_minimum_gap_hours",
                            )}
                            {renderSelect(
                              settingsData.split_shift_spread_threshold_hours ?? 0,
                              handleThresholdChange("split_shift_spread_threshold_hours"),
                              [
                                { value: 0, label: "No spread check" },
                                ...[8, 9, 10, 11, 12].map((h) => ({
                                  value: h,
                                  label: `${h} hr spread`,
                                })),
                              ],
                              172,
                              "split_shift_spread_threshold_hours",
                            )}
                          </>
                        ),
                      })}
                </>,
              )}
            </>
          )}
        </div>
      ) : (
        <div className="flex items-center justify-center min-h-[60vh]">
          <LoadSpinner />
        </div>
      )}
    </div>
  );
}

export default TimeEntriesSettings;
